import { useEffect, useState } from 'react';

const MINUTO = 60_000;

/** "1 min", "12 min", "1 h 12 min" — sempre arredondando para cima (nunca promete cedo demais). */
function duracao(restanteMs: number): string {
  const minutos = Math.max(1, Math.ceil(restanteMs / MINUTO));
  if (minutos < 60) return `${String(minutos)} min`;
  const resto = minutos % 60;
  return `${String(Math.floor(minutos / 60))} h${resto ? ` ${String(resto)} min` : ''}`;
}

/** "Liberado às 13:05 · faltam 12 min", com a hora no fuso pedido (padrão: o do navegador). */
export function textoEspera(liberadoEm: string, restanteMs: number, fuso?: string): string {
  const hora = new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    ...(fuso ? { timeZone: fuso } : {}),
  }).format(new Date(liberadoEm));
  const minutos = Math.ceil(restanteMs / MINUTO);
  return `Liberado às ${hora} · ${minutos <= 1 ? 'falta' : 'faltam'} ${duracao(restanteMs)}`;
}

interface Relogio {
  /** Alvo a que este relógio pertence (NaN = sem alvo). */
  fim: number;
  /** Último instante lido de verdade (`Date.now()`), nunca herdado de outro alvo. */
  agora: number;
  /** A espera foi vista positiva com relógio sincronizado e acabou com o componente na tela. */
  liberou: boolean;
}

/**
 * Quanto falta até `alvo`, acordando só quando o minuto mostrado muda (e na liberação exata).
 * Alvo novo (veio do servidor) ressincroniza o relógio na mesma renderização — o "agora" de um
 * alvo nunca é reaproveitado para outro. `liberou` só vale para a passagem de "esperando" para
 * "liberado" observada com o relógio em dia: trocar de alvo para o passado não anuncia nada.
 */
export function useEspera(alvo: string | null): { restante: number; liberou: boolean } {
  const fim = alvo ? Date.parse(alvo) : Number.NaN;
  const [relogio, setRelogio] = useState<Relogio>(() => ({
    fim,
    agora: Date.now(),
    liberou: false,
  }));
  if (!Object.is(relogio.fim, fim)) {
    // Estado derivado da prop (guarda o alvo anterior): a atualização durante a renderização faz o
    // React descartar esta saída e renderizar de novo já com o relógio lido agora.
    setRelogio(() => ({ fim, agora: Date.now(), liberou: false }));
  }
  const restante = Number.isFinite(relogio.fim) ? relogio.fim - relogio.agora : 0;

  useEffect(() => {
    if (!(restante > 0)) return undefined;
    const timer = setTimeout(
      () => {
        const n = Date.now();
        setRelogio({ fim, agora: n, liberou: fim - n <= 0 });
      },
      restante % MINUTO || MINUTO,
    );
    return () => {
      clearTimeout(timer);
    };
  }, [fim, restante]);

  return { restante, liberou: relogio.liberou && restante <= 0 };
}
